import { useCallback, useEffect, useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calculator, CheckCircle, Loader2 } from 'lucide-react';

const TH = 'text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary';
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

export function CommissionsTab({ onChange }) {
  const [month, setMonth] = useState(lastMonth());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/sales/commissions', { params: { month } }).then(r => setRows(r.data))
      .catch(err => toast.error(formatError(err.response?.data?.detail)));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const calculate = async () => {
    setBusy(true);
    try { const { data } = await api.post('/sales/commissions/calculate', { month }); toast.success(data.message); load(); onChange(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const pay = async (id) => {
    try { await api.put(`/sales/commissions/${id}/pay`, { note: '' }); toast.success('Marked as paid · added to Expenses'); load(); onChange(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const total = rows.reduce((s, r) => s + r.commission, 0);

  return (
    <Card className="border border-brand-border bg-white">
      <div className="p-3 flex flex-wrap items-center gap-2 border-b border-brand-border">
        <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44 h-9" data-testid="commission-month" />
        <Button onClick={calculate} disabled={busy} variant="outline" className="h-9 text-sm" data-testid="calculate-commission-btn">
          {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Calculator size={14} className="mr-1" />} Calculate 5%
        </Button>
        <p className="text-xs text-brand-text-secondary ml-auto">Total for {month}: <span className="font-semibold text-brand-text-primary" data-testid="commission-total">{money(total)}</span> · paid from company share</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-commissions-table">
          <thead><tr className="border-b border-brand-border bg-brand-bg/50">
            {['Sales Person', 'Client', 'Net Profit', 'Commission', 'Status', ''].map(h => <th key={h} className={TH}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="py-12 text-center text-brand-text-secondary">No commissions for this month. Click Calculate.</td></tr> : rows.map(r => (
              <tr key={r.id} className="border-b border-brand-border/50">
                <td className="py-3 px-4"><span className="font-mono text-xs text-brand-primary mr-1">{r.sales_code}</span>{r.sales_person_name}</td>
                <td className="py-3 px-4">{r.client_name} <span className="font-mono text-xs text-brand-text-secondary">{r.client_code}</span></td>
                <td className="py-3 px-4">{money(r.client_net_profit)}</td>
                <td className="py-3 px-4 font-semibold text-emerald-700">{money(r.commission)}</td>
                <td className="py-3 px-4"><Badge className={`border-0 text-[11px] ${r.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</Badge></td>
                <td className="py-3 px-4">{r.status === 'pending' && (
                  <Button size="sm" onClick={() => pay(r.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid={`pay-commission-${r.id}`}><CheckCircle size={13} className="mr-1" /> Mark Paid</Button>
                )}{r.status === 'paid' && <span className="text-xs text-brand-text-secondary">{r.paid_at?.slice(0, 10)}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
